'use client';

import { ReactNode } from 'react';
import { tv } from '@app/ui/_lib/utils';
import { Container } from '../Container';
import { SanityImage } from '../types';
import { Media } from '../Media';
import { TextSectionBlock, TextSectionBlockProps } from '../TextSectionBlock';
import { FeaturesListItem, FeaturesListItemProps } from '../FeaturesListItem';
import { Text } from '../Text';

const styles = tv({
	slots: {
		container: 'flex flex-col gap-spacing-lg',
		imageWrapper: 'rounded-lg overflow-hidden aspect-4/5',
		textWrapper: 'flex flex-col gap-spacing-lg',
		list: 'w-full',
	},
	variants: {
		hasImage: {
			true: {
				container: 'lg:grid lg:grid-cols-2',
				textWrapper: 'lg:py-responsive-xl',
			},
			false: {
				textWrapper: 'lg:flex-row',
			},
		},
		variant: {
			default: {},
			reversed: {
				imageWrapper: 'lg:[order:1]',
			},
			stacked: {},
		},
	},
	compoundVariants: [
		{
			hasImage: true,
			variant: 'default',
			className: { textWrapper: 'lg:pl-spacing-xl' },
		},
		{
			hasImage: true,
			variant: 'reversed',
			className: { textWrapper: 'lg:pr-spacing-xl' },
		},
		{
			hasImage: false,
			variant: 'default',
			className: { list: 'lg:pl-responsive-4xl' },
		},
		{
			hasImage: false,
			variant: 'reversed',
			className: { list: 'lg:pr-responsive-4xl' },
		},
	],
});

export type FeaturesListProps = {
	className?: string;
	variant?: 'default' | 'reversed' | 'stacked';
	image?: SanityImage;
	video?: string;
	textSectionBlock?: Omit<TextSectionBlockProps, 'size'>;
	featuresLabel?: ReactNode;
	features?: Omit<FeaturesListItemProps, 'size'>[];
};

export function FeaturesList(props: FeaturesListProps) {
	const variant = props.variant ?? 'default';
	const image = variant !== 'stacked' ? props.image : undefined;
	const hasImage: boolean = image !== undefined;

	const { container, imageWrapper, textWrapper, list } = styles({ hasImage, variant });

	return (
		<div className={props.className} data-component='FeaturesList'>
			<Container className={container()} size={variant === 'stacked' ? 'editorial-narrow' : 'default'}>
				{image && (
					<div className={imageWrapper()}>
						<Media className='h-full [&_img]:h-full' image={image} video={props.video} />
					</div>
				)}

				<div className={textWrapper({ className: variant === 'stacked' ? 'lg:flex-col' : '' })}>
					{props.textSectionBlock && (
						<div className={`${hasImage ? '' : 'lg:flex-[0_0_30%]'}`}>
							{props.textSectionBlock && (
								<TextSectionBlock
									size='md'
									{...props.textSectionBlock}
									buttons={variant === 'stacked' ? undefined : props.textSectionBlock.buttons}
								/>
							)}
						</div>
					)}

					{props.features && props.features.length > 0 && (
						<div className='lg:mt-auto'>
							{(hasImage || variant === 'stacked') && (
								<Text as='span' className='py-3 text-text-primary' styleType='label-sm'>
									{props.featuresLabel}
								</Text>
							)}

							<ul className={list()}>
								{props.features.map((item, i) => (
									<FeaturesListItem key={i} {...item} size={hasImage || variant === 'stacked' ? 'small' : 'medium'} />
								))}
							</ul>
						</div>
					)}

					{props.textSectionBlock?.buttons && variant === 'stacked' && (
						<TextSectionBlock size='md' buttons={props.textSectionBlock.buttons} />
					)}
				</div>
			</Container>
		</div>
	);
}
